import React, { useState, useEffect, useRef } from "react";
import { v4 as uuidv4 } from "uuid";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import VideoUploadManager from "./VideoUploadManager";
import { toast } from "sonner";
import { 
  Plus,
  Edit,
  Trash2,
  MoveUp,
  MoveDown,
  Route,
  Save,
  X,
  ArrowRight,
  ChevronDown,
  CheckCircle,
  Lock,
  Target,
  Award,
  MapPin,
  GripVertical,
  BookOpen,
  Upload,
  Clock
} from "lucide-react";
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Strict Mode Droppable Wrapper
const StrictModeDroppable = ({ children, ...props }) => {
  const [enabled, setEnabled] = useState(false);

  useEffect(() => {
    const animation = requestAnimationFrame(() => setEnabled(true));
    return () => {
      cancelAnimationFrame(animation);
      setEnabled(false);
    };
  }, []);

  if (!enabled) {
    return null;
  }

  return <Droppable {...props}>{children}</Droppable>;
};

const TrailsManager = ({ token, sectors, courses, assessments = [], fetchAdminData, onEditCourse, onDeleteCourse, onNewCourse, openCreateTrailSignal }) => {
  const [trails, setTrails] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingTrail, setEditingTrail] = useState(null);
  const [selectedSector, setSelectedSector] = useState("all");
  const [viewMode, setViewMode] = useState("trails"); // 'trails' | 'courses'
  const [coursePickerModal, setCoursePickerModal] = useState({ open: false, trail: null });
  const [finalAssessmentModal, setFinalAssessmentModal] = useState({ open: false, trail: null });
  const [createCourseModal, setCreateCourseModal] = useState({ open: false, trail: null });
  const [confirmDialog, setConfirmDialog] = useState({ open: false, title: '', message: '', onConfirm: null });
  const openConfirmDialog = (title, message, onConfirm) => setConfirmDialog({ open: true, title, message, onConfirm });
  const closeConfirmDialog = () => setConfirmDialog(d => ({ ...d, open: false, onConfirm: null }));
  const [imageUploading, setImageUploading] = useState(false);
  const [showVideoUploadManager, setShowVideoUploadManager] = useState(false);
  const [courseAssessmentAIConfig, setCourseAssessmentAIConfig] = useState({ num_questions: "10" });
  
  // Trail form state
  const [trailForm, setTrailForm] = useState({
    name: "",
    description: "",
    sectors: [],
    course_ids: [],
    requires_assessment: false,
    linked_assessment_id: null
  });

  const [courseForm, setCourseForm] = useState({
    title: "",
    description: "",
    thumbnail_url: "",
    external_url: "",
    sectors: [],
    segments: [],
    trail_order: 0,
    requires_assessment: false,
    linked_assessment_id: null,
    passing_score_required: 70.0
  });

  const [newSegment, setNewSegment] = useState({
    title: "",
    description: "",
    video_url: "",
    video_id: null,
    duration_minutes: 0,
    order: 1,
    video_type: "local"
  });

  // Available courses for selected sector
  const [availableCourses, setAvailableCourses] = useState([]);
  // Keep previous signal to detect edges (only open dialog when signal increments)
  const prevCreateSignalRef = useRef(openCreateTrailSignal);

  useEffect(() => {
    fetchTrails();
  }, [token]);

  // Open create-trail dialog when parent triggers the signal
  useEffect(() => {
    // Only open the dialog when the signal value changes (edge detection).
    // This avoids opening the modal when the component mounts or when
    // the parent passes an old/unchanged signal value (e.g., switching tabs).
    const prev = prevCreateSignalRef.current;
    if (openCreateTrailSignal && openCreateTrailSignal !== prev) {
      setEditingTrail(null);
      setDialogOpen(true);
    }
    prevCreateSignalRef.current = openCreateTrailSignal;
  }, [openCreateTrailSignal]);

  useEffect(() => {
    if (trailForm.sectors.length > 0) {
      const sortedCourses = [...courses].sort((a, b) => (a.trail_order || 0) - (b.trail_order || 0));
      setAvailableCourses(sortedCourses);
    } else {
      setAvailableCourses([]);
    }
  }, [trailForm.sectors, courses]);

  const availableFinalAssessments = React.useMemo(() => {
    if (trailForm.sectors.length === 0) return assessments;
    return (assessments || []).filter(assessment => {
      if (!assessment.sectors || assessment.sectors.length === 0) return true;
      return assessment.sectors.some(sector => trailForm.sectors.includes(sector));
    });
  }, [assessments, trailForm.sectors]);

  const selectedFinalAssessment = React.useMemo(() => {
    if (!trailForm.linked_assessment_id) return null;
    return (assessments || []).find(
      assessment => assessment.id === trailForm.linked_assessment_id
    ) || null;
  }, [assessments, trailForm.linked_assessment_id]);

  const selectedAssessmentLinkedTrails = React.useMemo(() => {
    if (!selectedFinalAssessment) return [];
    const links = selectedFinalAssessment.linked_trails || [];
    if (editingTrail?.id) {
      return links.filter(link => link.trail_id !== editingTrail.id);
    }
    return links;
  }, [selectedFinalAssessment, editingTrail]);

  const availableCoursesForPicker = React.useMemo(() => {
    if (!coursePickerModal.trail) return [];
    const trailCourseIds = coursePickerModal.trail.course_ids || [];
    return courses
      .filter(course => !trailCourseIds.includes(course.id))
      .sort((a, b) => (a.trail_order || 0) - (b.trail_order || 0));
  }, [coursePickerModal, courses]);

  const availableAssessmentsForTrail = React.useMemo(() => {
    if (!finalAssessmentModal.trail) return [];
    return (assessments || []).filter(assessment => {
      if (!assessment.sectors || assessment.sectors.length === 0) return true;
      return assessment.sectors.some(sector => (finalAssessmentModal.trail.sectors || []).includes(sector));
    });
  }, [finalAssessmentModal, assessments]);

  const fetchTrails = async () => {
    try {
      setLoading(true);
      const headers = { Authorization: `Bearer ${token}` };
      const response = await axios.get(`${API}/admin/trails`, { headers });
      setTrails(response.data || []);
    } catch (error) {
      console.error("Error fetching trails:", error);
      toast.error("Erro ao carregar trilhas");
    } finally {
      setLoading(false);
    }
  };

  const handleTrailSectorChange = (sectorName) => {
    setTrailForm(prev => ({
      ...prev,
      sectors: sectorName ? [sectorName] : [],
      course_ids: [],
      requires_assessment: false,
      linked_assessment_id: null
    }));
  };

  const handleCreateTrail = async (e) => {
    e.preventDefault();
    
    if (!trailForm.name || !trailForm.description || trailForm.sectors.length === 0) {
      toast.error("Preencha todos os campos obrigatórios");
      return;
    }

    if (trailForm.course_ids.length === 0) {
      toast.error("Adicione pelo menos um curso à trilha");
      return;
    }

    if (trailForm.requires_assessment && !trailForm.linked_assessment_id) {
      toast.error("Selecione a avaliação final da trilha");
      return;
    }

    try {
      const headers = { Authorization: `Bearer ${token}` };
      const payload = {
        ...trailForm,
        linked_assessment_id: trailForm.linked_assessment_id || null
      };
      
      if (editingTrail) {
        // Update existing trail
        await axios.put(`${API}/admin/trails/${editingTrail.id}`, payload, { headers });
        toast.success("Trilha atualizada com sucesso!");
      } else {
        // Create new trail
        await axios.post(`${API}/admin/trails`, payload, { headers });
        toast.success("Trilha criada com sucesso!");
      }
      
      resetForm();
      setDialogOpen(false);
      fetchTrails();
      fetchAdminData();
      
    } catch (error) {
      console.error("Error saving trail:", error);
      toast.error(error.response?.data?.detail || "Erro ao salvar trilha");
    }
  };

  const handleEditTrail = (trail) => {
    setEditingTrail(trail);
    setTrailForm({
      name: trail.name,
      description: trail.description,
      sectors: trail.sectors && trail.sectors.length ? trail.sectors : (trail.sector ? [trail.sector] : []),
      course_ids: trail.course_ids || [],
      requires_assessment: Boolean(trail.requires_assessment),
      linked_assessment_id: trail.linked_assessment_id || null
    });
    setDialogOpen(true);
  };

  const handleDeleteTrail = (trailId, trailName) => {
    openConfirmDialog(
      'Excluir Trilha',
      `Tem certeza que deseja excluir a trilha "${trailName}"? Esta ação não pode ser desfeita.`,
      async () => {
        try {
          const headers = { Authorization: `Bearer ${token}` };
          await axios.delete(`${API}/admin/trails/${trailId}`, { headers });
          toast.success("Trilha excluída com sucesso!");
          fetchTrails();
          fetchAdminData();
        } catch (error) {
          console.error("Error deleting trail:", error);
          toast.error("Erro ao excluir trilha");
        }
      }
    );
  };

  const resetForm = () => {
    setEditingTrail(null);
    setTrailForm({
      name: "",
      description: "",
      sectors: [],
      course_ids: [],
      requires_assessment: false,
      linked_assessment_id: null
    });
  };

  const addCourseToTrail = (courseId) => {
    if (!trailForm.course_ids.includes(courseId)) {
      setTrailForm(prev => ({
        ...prev,
        course_ids: [...prev.course_ids, courseId]
      }));
    }
  };

  const removeCourseFromTrail = (courseId) => {
    setTrailForm(prev => ({
      ...prev,
      course_ids: prev.course_ids.filter(id => id !== courseId)
    }));
  };

  const moveCourseUp = (index) => {
    if (index > 0) {
      const newCourses = [...trailForm.course_ids];
      [newCourses[index - 1], newCourses[index]] = [newCourses[index], newCourses[index - 1]];
      setTrailForm(prev => ({ ...prev, course_ids: newCourses }));
      if (editingTrail) {
        setEditingTrail(prev => prev ? { ...prev, course_ids: newCourses } : prev);
        saveTrailOrder(newCourses, editingTrail, { refresh: false });
      }
    }
  };

  const moveCourseDown = (index) => {
    if (index < trailForm.course_ids.length - 1) {
      const newCourses = [...trailForm.course_ids];
      [newCourses[index], newCourses[index + 1]] = [newCourses[index + 1], newCourses[index]];
      setTrailForm(prev => ({ ...prev, course_ids: newCourses }));
      if (editingTrail) {
        setEditingTrail(prev => prev ? { ...prev, course_ids: newCourses } : prev);
        saveTrailOrder(newCourses, editingTrail, { refresh: false });
      }
    }
  };

  const saveTrailOrder = async (newCourseIds, targetTrail = editingTrail, options = { refresh: true, showToast: true }) => {
    if (!targetTrail) return;
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const payload = {
        name: targetTrail.name,
        description: targetTrail.description,
        sectors: targetTrail.sectors && targetTrail.sectors.length ? targetTrail.sectors : (targetTrail.sector ? [targetTrail.sector] : []),
        course_ids: newCourseIds,
        requires_assessment: editingTrail?.requires_assessment ?? targetTrail?.requires_assessment ?? false,
        linked_assessment_id: editingTrail?.linked_assessment_id ?? targetTrail?.linked_assessment_id ?? null
      };
      await axios.put(`${API}/admin/trails/${targetTrail.id}`, payload, { headers });
      if (options?.showToast !== false) {
        toast.success('Ordem da trilha atualizada');
      }
      // refresh trails and courses so global listing reflects the new order
      // Also update each course's `trail_order` so public dashboard sorts by it
      try {
        await Promise.all(newCourseIds.map(async (courseId, idx) => {
          const course = courses.find(c => c.id === courseId);
          if (!course) return;
          const coursePayload = {
            title: course.title || '',
            description: course.description || '',
            thumbnail_url: course.thumbnail_url || '',
            external_url: course.external_url || '',
            sectors: course.sectors || [],
            segments: course.segments || [],
            requires_assessment: course.requires_assessment || false,
            linked_assessment_id: course.linked_assessment_id || null,
            passing_score_required: course.passing_score_required || 70.0,
            trail_order: idx
          };
          await axios.put(`${API}/courses/${courseId}`, coursePayload, { headers });
        }));
      } catch (err) {
        console.error('Erro ao atualizar ordem dos cursos individualmente:', err);
      }

      if (options?.refresh === false) {
        setTrails(prev => prev.map(t => t.id === targetTrail.id ? { ...t, course_ids: newCourseIds } : t));
      } else {
        fetchTrails();
        fetchAdminData();
      }
      return true;
    } catch (err) {
      console.error('Erro ao salvar ordem da trilha:', err);
      toast.error('Não foi possível salvar a ordem da trilha');
      return false;
    }
  };

  const handleSequenceMove = async (trail, courseId, direction) => {
    if (!trail?.course_ids) return;
    const currentIndex = trail.course_ids.indexOf(courseId);
    if (currentIndex === -1) return;

    const newIndex = direction === "up" ? currentIndex - 1 : currentIndex + 1;
    if (newIndex < 0 || newIndex >= trail.course_ids.length) return;

    const reorderedIds = Array.from(trail.course_ids);
    const [moved] = reorderedIds.splice(currentIndex, 1);
    reorderedIds.splice(newIndex, 0, moved);

    // Update local trail object immediately for instant feedback
    trail.course_ids = reorderedIds;

    const success = await saveTrailOrder(reorderedIds, trail, { refresh: false });
    if (!success) return;

    if (editingTrail?.id === trail.id) {
      setTrailForm(prev => ({ ...prev, course_ids: reorderedIds }));
      setEditingTrail(prev => prev ? { ...prev, course_ids: reorderedIds } : prev);
    }
  };

  const handleFormCourseDragEnd = (result) => {
    if (!result.destination) {
      return;
    }

    const items = Array.from(trailForm.course_ids);
    const [reorderedItem] = items.splice(result.source.index, 1);
    items.splice(result.destination.index, 0, reorderedItem);

    setTrailForm(prev => ({ ...prev, course_ids: items }));
    if (editingTrail) {
      setEditingTrail(prev => prev ? { ...prev, course_ids: items } : prev);
      saveTrailOrder(items, editingTrail, { refresh: false });
    }
  };

  const openCoursePicker = (trail) => {
    setCoursePickerModal({ open: true, trail });
  };

  const closeCoursePicker = () => {
    setCoursePickerModal({ open: false, trail: null });
  };

  const openFinalAssessmentPicker = (trail) => {
    setFinalAssessmentModal({ open: true, trail });
  };

  const closeFinalAssessmentPicker = () => {
    setFinalAssessmentModal({ open: false, trail: null });
  };

  const resetCourseForm = (trail = null) => {
    setCourseForm({
      title: "",
      description: "",
      thumbnail_url: "",
      external_url: "",
      sectors: trail?.sectors && trail.sectors.length ? trail.sectors : (trail?.sector ? [trail.sector] : []),
      segments: [],
      trail_order: trail?.course_ids?.length || 0,
      requires_assessment: false,
      linked_assessment_id: null,
      passing_score_required: 70.0
    });
    setNewSegment({
      title: "",
      description: "",
      video_url: "",
      video_id: null,
      duration_minutes: 0,
      order: 1,
      video_type: "local"
    });
    setCourseAssessmentAIConfig({ num_questions: "10" });
  };

  const openCreateCourseModal = (trail) => {
    resetCourseForm(trail);
    setCreateCourseModal({ open: true, trail });
  };

  const closeCreateCourseModal = () => {
    setCreateCourseModal({ open: false, trail: null });
    setShowVideoUploadManager(false);
  };

  const handleCourseSegmentDragEnd = (result) => {
    if (!result.destination) {
      return;
    }

    const items = Array.from(courseForm.segments);
    const [reorderedItem] = items.splice(result.source.index, 1);
    items.splice(result.destination.index, 0, reorderedItem);

    setCourseForm(prev => ({
      ...prev,
      segments: items.map((segment, index) => ({
        ...segment,
        order: index + 1
      }))
    }));
  };

  const addSegment = () => {
    if (!newSegment.title) {
      toast.error("Título é obrigatório");
      return;
    }

    if (!newSegment.video_url && !newSegment.video_id) {
      toast.error("Forneça um vídeo (upload ou URL do YouTube)");
      return;
    }

    setCourseForm(prev => ({
      ...prev,
      segments: [...prev.segments, { ...newSegment, id: uuidv4() }]
    }));

    setNewSegment({
      title: "",
      description: "",
      video_url: "",
      video_id: null,
      duration_minutes: 0,
      order: courseForm.segments.length + 2,
      video_type: "local"
    });
  };

  const removeSegment = (index) => {
    setCourseForm(prev => ({
      ...prev,
      segments: prev.segments.filter((_, i) => i !== index)
    }));
  };

  const handleImageUpload = async (file) => {
    if (!file) return;

    setImageUploading(true);
    try {
      const formData = new FormData();
      formData.append("file", file);

      const headers = { Authorization: `Bearer ${token}` };
      const response = await axios.post(`${API}/upload-image`, formData, { headers });
      const imageUrl = `${BACKEND_URL}${response.data.file_url}`;

      setCourseForm(prev => ({
        ...prev,
        thumbnail_url: imageUrl
      }));

      toast.success("Imagem enviada com sucesso!");
    } catch (error) {
      console.error("Error uploading image:", error);
      toast.error("Erro ao enviar imagem");
    } finally {
      setImageUploading(false);
    }
  };

  const handleAddCourseToTrail = async (courseId) => {
    const targetTrail = coursePickerModal.trail;
    if (!targetTrail) return;
    const newCourseIds = [...(targetTrail.course_ids || []), courseId];
    const success = await saveTrailOrder(newCourseIds, targetTrail, { refresh: false });
    if (success === false) return;
    setTrails(prev => prev.map(t => t.id === targetTrail.id ? { ...t, course_ids: newCourseIds } : t));
    if (editingTrail?.id === targetTrail.id) {
      setTrailForm(prev => ({ ...prev, course_ids: newCourseIds }));
      setEditingTrail(prev => prev ? { ...prev, course_ids: newCourseIds } : prev);
    }
    toast.success("Curso adicionado à trilha");
    closeCoursePicker();
  };

  const handleCreateCourseForTrail = async (e) => {
    e.preventDefault();

    const targetTrail = createCourseModal.trail;
    if (!targetTrail) return;

    if (!courseForm.title || !courseForm.description) {
      toast.error("Preencha todos os campos obrigatórios");
      return;
    }

    let finalSegments = [...courseForm.segments];
    if (newSegment.title && (newSegment.video_id || newSegment.video_url)) {
      const isDuplicate = finalSegments.some(seg =>
        seg.title === newSegment.title &&
        (seg.video_id === newSegment.video_id || seg.video_url === newSegment.video_url)
      );

      if (!isDuplicate) {
        finalSegments.push({ ...newSegment, id: uuidv4() });
      }
    }

    if (finalSegments.length === 0) {
      toast.error("Adicione pelo menos um módulo com vídeo");
      return;
    }

    try {
      const headers = { Authorization: `Bearer ${token}` };
      const normalizedSegments = finalSegments.map((seg, index) => ({
        id: seg.id,
        title: seg.title || "",
        description: seg.description || "",
        video_url: seg.video_url || null,
        video_id: seg.video_id || null,
        video_type: seg.video_type || "youtube",
        duration_minutes: seg.duration_minutes || 0,
        order: index + 1
      }));
      const externalUrl = normalizedSegments.find(seg => seg.video_url)?.video_url || "";
      const trailCourseIds = targetTrail.course_ids || [];
      const courseData = {
        ...courseForm,
        external_url: externalUrl,
        sectors: targetTrail.sectors && targetTrail.sectors.length ? targetTrail.sectors : (targetTrail.sector ? [targetTrail.sector] : []),
        segments: normalizedSegments,
        trail_order: trailCourseIds.length,
        requires_assessment: courseForm.requires_assessment || false,
        linked_assessment_id: courseForm.linked_assessment_id || null,
        passing_score_required: courseForm.passing_score_required || 70.0
      };

      const response = await axios.post(`${API}/courses`, courseData, { headers });
      const createdCourse = response.data;
      const newCourseIds = [...trailCourseIds, createdCourse.id];

      await saveTrailOrder(newCourseIds, targetTrail, { refresh: false, showToast: false });
      toast.success("Curso criado e vinculado à trilha");
      closeCreateCourseModal();
      resetCourseForm();
      fetchAdminData();
    } catch (error) {
      console.error("Error creating course:", error);
      toast.error(error.response?.data?.detail || "Erro ao criar curso");
    }
  };

  const handleLinkFinalAssessment = async (assessmentId) => {
    const trail = finalAssessmentModal.trail;
    if (!trail) return;
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const payload = {
        name: trail.name,
        description: trail.description,
        sectors: trail.sectors && trail.sectors.length ? trail.sectors : (trail.sector ? [trail.sector] : []),
        course_ids: trail.course_ids || [],
        requires_assessment: true,
        linked_assessment_id: assessmentId
      };
      await axios.put(`${API}/admin/trails/${trail.id}`, payload, { headers });
      toast.success("Avaliação vinculada à trilha");
      setTrails(prev => prev.map(t => t.id === trail.id ? { ...t, requires_assessment: true, linked_assessment_id: assessmentId } : t));
      if (editingTrail?.id === trail.id) {
        setEditingTrail(prev => prev ? { ...prev, requires_assessment: true, linked_assessment_id: assessmentId } : prev);
        setTrailForm(prev => ({ ...prev, requires_assessment: true, linked_assessment_id: assessmentId }));
      }
      closeFinalAssessmentPicker();
    } catch (error) {
      console.error("Erro ao vincular avaliação final:", error);
      toast.error("Não foi possível vincular a avaliação");
    }
  };

  const handleRemoveFinalAssessment = async (trail) => {
    if (!trail) return;
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const payload = {
        name: trail.name,
        description: trail.description,
        sectors: trail.sectors && trail.sectors.length ? trail.sectors : (trail.sector ? [trail.sector] : []),
        course_ids: trail.course_ids || [],
        requires_assessment: false,
        linked_assessment_id: null
      };
      await axios.put(`${API}/admin/trails/${trail.id}`, payload, { headers });
      toast.success("Avaliação final removida da trilha");
      setTrails(prev => prev.map(t => t.id === trail.id ? { ...t, requires_assessment: false, linked_assessment_id: null } : t));
      if (editingTrail?.id === trail.id) {
        setEditingTrail(prev => prev ? { ...prev, requires_assessment: false, linked_assessment_id: null } : prev);
        setTrailForm(prev => ({ ...prev, requires_assessment: false, linked_assessment_id: null }));
      }
    } catch (error) {
      console.error("Erro ao remover avaliação final:", error);
      toast.error("Não foi possível remover a avaliação final");
    }
  };

  const getCourseById = (courseId) => {
    return courses.find(c => c.id === courseId);
  };

  const getOtherTrailsWithCourse = (courseId, currentTrailId) => {
    return trails.filter(t => t.id !== currentTrailId && (t.course_ids || []).includes(courseId));
  };

  const confirmCourseAction = (courseId, currentTrailId, actionLabel) => {
    const others = getOtherTrailsWithCourse(courseId, currentTrailId);
    if (others.length === 0) return true;
    const names = others.map(t => t.name).join(", ");
    return window.confirm(`Este curso também faz parte da(s) trilha(s): ${names}. Deseja ${actionLabel}?`);
  };

  const getSectorDisplayName = (sectorName) => {
    const sector = sectors.find(s => s.name === sectorName);
    return sector ? sector.display_name : sectorName;
  };

  const handleTrailCourseEdit = (courseId, trailId) => {
    const course = getCourseById(courseId);
    if (!course) {
      toast.error("Curso não encontrado");
      return;
    }
    if (!confirmCourseAction(courseId, trailId, "editar mesmo assim")) return;
    if (onEditCourse) {
      onEditCourse(course);
    }
  };

  const handleTrailCourseDelete = async (courseId, trailId) => {
    const trail = trails.find(t => t.id === trailId);
    if (!trail) {
      toast.error("Trilha não encontrada");
      return;
    }
    const newCourseIds = trail.course_ids.filter(id => id !== courseId);
    const success = await saveTrailOrder(newCourseIds, trail, { refresh: false });
    if (!success) return;
    setTrails(prev => prev.map(t => t.id === trailId ? { ...t, course_ids: newCourseIds } : t));
    if (editingTrail?.id === trailId) {
      setTrailForm(prev => ({ ...prev, course_ids: newCourseIds }));
      setEditingTrail(prev => prev ? { ...prev, course_ids: newCourseIds } : prev);
    }
    toast.success("Curso removido da trilha");
  };

  const handleDeleteCourseFromSystem = (courseId, trailId) => {
    const course = getCourseById(courseId);
    if (!course) {
      toast.error("Curso não encontrado");
      return;
    }
    if (!confirmCourseAction(courseId, trailId, "excluir o curso do sistema mesmo assim")) return;
    if (onDeleteCourse) {
      onDeleteCourse(courseId, course.title);
      return;
    }
    toast.error("Ação indisponível para excluir o curso do sistema");
  };

  const filteredTrails = selectedSector === "all" 
    ? trails 
    : trails.filter(t => (t.sectors || (t.sector ? [t.sector] : [])).includes(selectedSector));

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="text-center">
          <div className="loading-spinner w-12 h-12 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Carregando trilhas...</p>
        </div>
      </div>
    );
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
      {/* Trail create/edit dialog (opened via external signal) */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto" style={{ background: '#12121a', border: '1px solid rgba(255,255,255,0.08)', color: '#e0e0f0' }}>
          <DialogHeader style={{ paddingBottom: '0.75rem', borderBottom: '1px solid rgba(255,255,255,0.06)', marginBottom: '0.5rem' }}>
            <DialogTitle style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', fontFamily: "'Space Grotesk', sans-serif", fontSize: '0.96rem', fontWeight: 700, color: '#e8e8f0' }}>
              <div style={{ width: 26, height: 26, borderRadius: 7, flexShrink: 0, background: 'rgba(59,130,246,0.12)', border: '1px solid rgba(59,130,246,0.22)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <Route size={13} style={{ color: '#60a5fa' }} />
              </div>
              {editingTrail ? 'Editar Trilha' : 'Nova Trilha'}
            </DialogTitle>
            <DialogDescription style={{ fontSize: '0.73rem', color: '#4a4a6a', fontFamily: "'Inter', sans-serif", paddingLeft: '2.35rem', marginTop: '0.1rem' }}>
              Configure a trilha de aprendizado com sequência de cursos
            </DialogDescription>
          </DialogHeader>

          <form onSubmit={handleCreateTrail} className="space-y-4">
            <div className="space-y-3">
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', paddingBottom: '0.45rem', borderBottom: '1px solid rgba(255,255,255,0.05)' }}>
                <div style={{ width: 20, height: 20, borderRadius: 5, flexShrink: 0, background: 'rgba(59,130,246,0.1)', border: '1px solid rgba(59,130,246,0.2)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <Route size={10} style={{ color: '#60a5fa' }} />
                </div>
                <span style={{ fontSize: '0.67rem', fontWeight: 700, color: '#5a5a8a', fontFamily: "'Inter', sans-serif", textTransform: 'uppercase', letterSpacing: '0.09em' }}>Informações Básicas</span>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="form-group">
                  <Label htmlFor="trail-name" className="form-label">Nome da Trilha *</Label>
                  <Input
                    id="trail-name"
                    value={trailForm.name}
                    onChange={(e) => setTrailForm(prev => ({ ...prev, name: e.target.value }))}
                    required
                    placeholder="Ex: Trilha Técnica Avançada"
                    data-testid="trail-name-input"
                    className="dialog-input"
                  />
                </div>
                <div className="form-group">
                  <Label htmlFor="trail-sector" className="form-label">Setor *</Label>
                  <Select value={trailForm.sectors[0] || ""} onValueChange={handleTrailSectorChange} data-testid="trail-sector-select">
                    <SelectTrigger className="form-input"><SelectValue placeholder="Selecione o setor" /></SelectTrigger>
                    <SelectContent style={{ background: '#1a1a2a', border: '1px solid rgba(255,255,255,0.1)', color: '#e0e0f0' }}>
                      {sectors.map(sector => (
                        <SelectItem key={sector.name} value={sector.name}>{sector.display_name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="form-group">
                <Label htmlFor="trail-description" className="form-label">Descrição *</Label>
                <Textarea
                  id="trail-description"
                  value={trailForm.description}
                  onChange={(e) => setTrailForm(prev => ({ ...prev, description: e.target.value }))}
                  required
                  rows={3}
                  placeholder="Descreva o objetivo e conteúdo desta trilha de aprendizado"
                  data-testid="trail-description-input"
                  className="dialog-textarea"
                />
              </div>
            </div>

                {trailForm.sectors.length > 0 && (
                  <div className="space-y-4">
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', paddingBottom: '0.45rem', borderBottom: '1px solid rgba(255,255,255,0.05)' }}>
                      <div style={{ width: 20, height: 20, borderRadius: 5, flexShrink: 0, background: 'rgba(59,130,246,0.1)', border: '1px solid rgba(59,130,246,0.2)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <BookOpen size={10} style={{ color: '#60a5fa' }} />
                      </div>
                      <span style={{ fontSize: '0.67rem', fontWeight: 700, color: '#5a5a8a', fontFamily: "'Inter', sans-serif", textTransform: 'uppercase', letterSpacing: '0.09em' }}>Cursos da Trilha</span>
                      {trailForm.course_ids.length > 0 && (
                        <span style={{ fontSize: '0.65rem', fontWeight: 600, color: '#60a5fa', background: 'rgba(59,130,246,0.1)', border: '1px solid rgba(59,130,246,0.2)', padding: '1px 7px', borderRadius: 4, fontFamily: 'monospace' }}>
                          {trailForm.course_ids.length} curso{trailForm.course_ids.length !== 1 ? 's' : ''}
                        </span>
                      )}
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div style={{ border: '1px solid rgba(255,255,255,0.07)', borderRadius: 9, padding: '0.65rem', background: 'rgba(255,255,255,0.02)' }}>
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '0.5rem' }}>
                          <span style={{ fontSize: '0.75rem', fontWeight: 600, color: '#8a8aaa', fontFamily: "'Inter', sans-serif" }}>Disponíveis</span>
                          <span style={{ fontSize: '0.65rem', fontWeight: 600, color: '#4a4a6a', background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.08)', padding: '1px 6px', borderRadius: 4, fontFamily: 'monospace' }}>{availableCourses.length}</span>
                        </div>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.3rem', maxHeight: '16rem', overflowY: 'auto' }}>
                          {availableCourses.map(course => (
                            <div
                              key={course.id}
                              style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '0.45rem 0.55rem', borderRadius: 7, background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.06)', cursor: 'pointer', transition: 'border-color 0.1s ease' }}
                              onClick={() => addCourseToTrail(course.id)}
                            >
                              <div style={{ minWidth: 0 }}>
                                <p style={{ fontSize: '0.78rem', fontWeight: 600, color: '#c0c0d8', fontFamily: "'Inter', sans-serif", overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={course.title}>{course.title}</p>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', marginTop: '0.1rem' }}>
                                  <span style={{ fontSize: '0.65rem', color: '#4a4a6a', fontFamily: 'monospace' }}>{course.segments?.length || 0}m</span>
                                  <span style={{ fontSize: '0.65rem', color: '#4a4a6a', fontFamily: 'monospace' }}>{course.total_duration_minutes}min</span>
                                </div>
                              </div>
                              <button type="button" style={{ width: 22, height: 22, borderRadius: 5, flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'rgba(59,130,246,0.1)', border: '1px solid rgba(59,130,246,0.2)', color: '#60a5fa', cursor: 'pointer' }}>
                                <Plus size={11} />
                              </button>
                            </div>
                          ))}
                          {availableCourses.length === 0 && (
                            <p style={{ fontSize: '0.75rem', color: '#3a3a5a', textAlign: 'center', padding: '0.75rem 0', fontFamily: "'Inter', sans-serif" }}>Todos os cursos já estão na trilha.</p>
                          )}
                        </div>
                      </div>

                      <div style={{ border: '1px solid rgba(255,255,255,0.07)', borderRadius: 9, padding: '0.65rem', background: 'rgba(255,255,255,0.02)' }}>
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '0.5rem' }}>
                          <span style={{ fontSize: '0.75rem', fontWeight: 600, color: '#8a8aaa', fontFamily: "'Inter', sans-serif" }}>Na trilha</span>
                          <span style={{ fontSize: '0.65rem', fontWeight: 600, color: '#4a4a6a', background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.08)', padding: '1px 6px', borderRadius: 4, fontFamily: 'monospace' }}>
                            {trailForm.course_ids.length} curso{trailForm.course_ids.length !== 1 ? 's' : ''}
                          </span>
                        </div>
                        {trailForm.course_ids.length === 0 ? (
                          <div className="p-4 text-center text-gray-500">
                            <p>Nenhum curso adicionado.</p>
                            <p className="text-sm">Selecione cursos disponíveis para montar a sequência.</p>
                          </div>
                        ) : (
                          <DragDropContext onDragEnd={handleFormCourseDragEnd}>
                            <StrictModeDroppable droppableId="trail-form-courses">
                              {(provided) => (
                                <div
                                  className="space-y-2"
                                  ref={provided.innerRef}
                                  {...provided.droppableProps}
                                >
                                  {trailForm.course_ids.map((courseId, index) => {
                                    const course = getCourseById(courseId);
                                    if (!course) return null;
                                    return (
                                      <Draggable key={courseId} draggableId={`trail-form-${courseId}`} index={index}>
                                        {(dragProvided, snapshot) => (
                                          <div
                                            ref={dragProvided.innerRef}
                                            {...dragProvided.draggableProps}
                                            style={{ ...dragProvided.draggableProps.style, background: snapshot.isDragging ? 'rgba(59,130,246,0.08)' : 'rgba(255,255,255,0.03)', border: snapshot.isDragging ? '1px solid rgba(59,130,246,0.25)' : '1px solid rgba(255,255,255,0.07)', borderRadius: 8, padding: '0.5rem 0.6rem', display: 'flex', alignItems: 'flex-start', gap: '0.5rem' }}
                                          >
                                            <div
                                              {...dragProvided.dragHandleProps}
                                              style={{ color: '#3a3a5a', cursor: 'grab', paddingTop: '0.15rem', flexShrink: 0 }}
                                              title="Arraste para reordenar"
                                            >
                                              <GripVertical size={14} />
                                            </div>
                                            <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '0.5rem', minWidth: 0 }}>
                                              <div style={{ minWidth: 0 }}>
                                                <p style={{ fontSize: '0.78rem', fontWeight: 600, color: '#c0c0d8', fontFamily: "'Inter', sans-serif", overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={course.title}>{course.title}</p>
                                                <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', marginTop: '0.1rem' }}>
                                                  <span style={{ fontSize: '0.65rem', color: '#4a4a6a', fontFamily: 'monospace' }}>{course.segments?.length || 0}m</span>
                                                  <span style={{ fontSize: '0.65rem', color: '#4a4a6a', fontFamily: 'monospace' }}>{course.total_duration_minutes}min</span>
                                                </div>
                                              </div>
                                              <div style={{ display: 'flex', alignItems: 'center', gap: '0.25rem', flexShrink: 0 }}>
                                                <button type="button" onClick={() => moveCourseUp(index)} disabled={index === 0} style={{ width: 22, height: 22, borderRadius: 5, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.08)', color: '#5a5a8a', cursor: index === 0 ? 'not-allowed' : 'pointer', opacity: index === 0 ? 0.4 : 1 }}><MoveUp size={11} /></button>
                                                <button type="button" onClick={() => moveCourseDown(index)} disabled={index === trailForm.course_ids.length - 1} style={{ width: 22, height: 22, borderRadius: 5, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.08)', color: '#5a5a8a', cursor: index === trailForm.course_ids.length - 1 ? 'not-allowed' : 'pointer', opacity: index === trailForm.course_ids.length - 1 ? 0.4 : 1 }}><MoveDown size={11} /></button>
                                                <button type="button" onClick={() => removeCourseFromTrail(courseId)} style={{ width: 22, height: 22, borderRadius: 5, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'rgba(239,68,68,0.06)', border: '1px solid rgba(239,68,68,0.12)', color: '#f87171', cursor: 'pointer' }}><Trash2 size={11} /></button>
                                              </div>
                                            </div>
                                          </div>
                                        )}
                                      </Draggable>
                                    );
                                  })}
                                  {provided.placeholder}
                                </div>
                              )}
                            </StrictModeDroppable>
                          </DragDropContext>
                        )}
                      </div>
                    </div>

                    <div style={{ border: '1px solid rgba(255,255,255,0.07)', borderRadius: 9, padding: '0.75rem', background: 'rgba(255,255,255,0.02)' }}>
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '0.75rem' }}>
                        <div>
                          <span style={{ fontSize: '0.82rem', fontWeight: 600, color: '#c0c0d8', fontFamily: "'Inter', sans-serif", display: 'block', marginBottom: '0.2rem' }}>Avaliação final da trilha</span>
                          <p style={{ fontSize: '0.72rem', color: '#4a4a6a', fontFamily: "'Inter', sans-serif", margin: 0 }}>
                            Exija que os colaboradores concluam todos os cursos e realizem uma avaliação única para finalizar a trilha.
                          </p>
                        </div>
                        <Switch
                          checked={trailForm.requires_assessment}
                          onCheckedChange={(checked) =>
                            setTrailForm(prev => ({
                              ...prev,
                              requires_assessment: checked,
                              linked_assessment_id: checked ? prev.linked_assessment_id : null
                            }))
                          }
                        />
                      </div>

                      {trailForm.requires_assessment && (
                        <div style={{ marginTop: '0.75rem', display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                          <div>
                            <span style={{ fontSize: '0.72rem', fontWeight: 600, color: '#6a6a9a', fontFamily: "'Inter', sans-serif", textTransform: 'uppercase', letterSpacing: '0.06em' }}>Selecionar avaliação *</span>
                            <p style={{ fontSize: '0.68rem', color: '#3a3a5a', fontFamily: "'Inter', sans-serif", margin: '0.1rem 0 0' }}>
                              Apenas avaliações dos setores escolhidos serão listadas.
                            </p>
                          </div>

                          {availableFinalAssessments.length === 0 ? (
                            <div style={{ padding: '0.65rem', border: '1px solid rgba(245,158,11,0.2)', borderRadius: 8, background: 'rgba(245,158,11,0.06)', fontSize: '0.75rem', color: '#f59e0b', fontFamily: "'Inter', sans-serif" }}>
                              Nenhuma avaliação disponível para estes setores. Crie uma nova avaliação antes de continuar.
                            </div>
                          ) : (
                            <Select
                              value={trailForm.linked_assessment_id || undefined}
                              onValueChange={(value) => setTrailForm(prev => ({ ...prev, linked_assessment_id: value }))}
                            >
                              <SelectTrigger className="form-input"><SelectValue placeholder="Selecione a avaliação final" /></SelectTrigger>
                              <SelectContent style={{ background: '#1a1a2a', border: '1px solid rgba(255,255,255,0.1)', color: '#e0e0f0' }}>
                                {availableFinalAssessments.map(assessment => {
                                  const linkedTrails = assessment.linked_trails || [];
                                  return (
                                    <SelectItem key={assessment.id} value={assessment.id} className="py-2">
                                      <div className="flex flex-col text-left">
                                        <span style={{ fontWeight: 600, color: '#c0c0d8', fontSize: '0.82rem' }}>{assessment.title}</span>
                                        <span style={{ fontSize: '0.68rem', color: '#5a5a8a' }}>
                                          {assessment.sectors?.length
                                            ? `Setores: ${assessment.sectors.map(sec => getSectorDisplayName(sec)).join(", ")}`
                                            : "Todos os setores"}
                                        </span>
                                        {linkedTrails.length > 0 && (
                                          <span style={{ fontSize: '0.68rem', color: '#f59e0b' }}>
                                            Já vinculada a {linkedTrails.length} trilha{linkedTrails.length !== 1 ? 's' : ''}
                                          </span>
                                        )}
                                      </div>
                                    </SelectItem>
                                  );
                                })}
                              </SelectContent>
                            </Select>
                          )}

                          {selectedFinalAssessment && (
                            <div style={{ padding: '0.6rem 0.75rem', border: '1px solid rgba(59,130,246,0.2)', borderRadius: 8, background: 'rgba(59,130,246,0.05)', display: 'flex', flexDirection: 'column', gap: '0.15rem' }}>
                              <p style={{ fontSize: '0.75rem', fontWeight: 600, color: '#93c5fd', margin: 0, fontFamily: "'Inter', sans-serif" }}>
                                Avaliação selecionada: {selectedFinalAssessment.title}
                              </p>
                              <p style={{ fontSize: '0.7rem', color: '#4a4a6a', margin: 0, fontFamily: 'monospace' }}>
                                Nota mínima: {selectedFinalAssessment.passing_grade}% · Tentativas: {selectedFinalAssessment.max_attempts}
                              </p>
                              {selectedAssessmentLinkedTrails.length > 0 ? (
                                <p style={{ fontSize: '0.7rem', color: '#4a4a6a', margin: 0, fontFamily: "'Inter', sans-serif" }}>
                                  Também usada em: {selectedAssessmentLinkedTrails.map(link => link.trail_name).join(", ")}
                                </p>
                              ) : (
                                <p style={{ fontSize: '0.7rem', color: '#3a3a5a', margin: 0, fontFamily: "'Inter', sans-serif" }}>Esta avaliação ainda não está vinculada a outras trilhas.</p>
                              )}
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                )}

                <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '0.5rem', paddingTop: '0.875rem', borderTop: '1px solid rgba(255,255,255,0.06)' }}>
                  <Button
                    type="button"
                    onClick={() => { setDialogOpen(false); resetForm(); }}
                    data-testid="cancel-trail-btn"
                    style={{ display: 'inline-flex', alignItems: 'center', padding: '0.42rem 0.95rem', borderRadius: 8, height: 'auto', background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.09)', color: '#6a6a9a', fontSize: '0.79rem', fontWeight: 600, cursor: 'pointer', fontFamily: "'Inter', sans-serif" }}
                  >
                    Cancelar
                  </Button>
                  <Button
                    type="submit"
                    disabled={trailForm.course_ids.length === 0}
                    data-testid="save-trail-btn"
                    style={{ display: 'inline-flex', alignItems: 'center', gap: '0.35rem', padding: '0.42rem 0.95rem', borderRadius: 8, height: 'auto', background: trailForm.course_ids.length === 0 ? 'rgba(255,255,255,0.04)' : 'rgba(59,130,246,0.18)', border: trailForm.course_ids.length === 0 ? '1px solid rgba(255,255,255,0.07)' : '1px solid rgba(59,130,246,0.3)', color: trailForm.course_ids.length === 0 ? '#3a3a5a' : '#60a5fa', fontSize: '0.79rem', fontWeight: 600, cursor: trailForm.course_ids.length === 0 ? 'not-allowed' : 'pointer', fontFamily: "'Inter', sans-serif" }}
                  >
                    <Save size={13} />
                    {editingTrail ? 'Atualizar' : 'Criar'} Trilha
                  </Button>
                </div>
          </form>
          </DialogContent>
      </Dialog>

      {/* Filter Bar */}
      <div style={{
        display: 'flex', alignItems: 'center', gap: '0.75rem', flexWrap: 'wrap',
        padding: '0.45rem 0.75rem',
        background: '#12121a',
        border: '1px solid rgba(255,255,255,0.07)',
        borderRadius: 10,
      }}>
        <span style={{
          fontSize: '0.68rem', color: '#3a3a5a', fontFamily: "'Inter', sans-serif",
          fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.1em', flexShrink: 0,
        }}>
          Setor
        </span>
        <Select value={selectedSector} onValueChange={setSelectedSector}>
          <SelectTrigger style={{
            width: 200, height: 28, fontSize: '0.78rem',
            background: 'rgba(255,255,255,0.04)',
            border: '1px solid rgba(255,255,255,0.08)',
            borderRadius: 7, color: '#c8c8d8',
          }}>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Todos os setores</SelectItem>
            {sectors.map(sector => (
              <SelectItem key={sector.name} value={sector.name}>
                {sector.display_name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        {filteredTrails.length > 0 && (
          <span style={{
            display: 'inline-flex', alignItems: 'center',
            padding: '1px 8px', borderRadius: 5,
            background: 'rgba(255,255,255,0.04)',
            border: '1px solid rgba(255,255,255,0.07)',
            fontSize: '0.68rem', color: '#4a4a6a', fontFamily: "'Inter', sans-serif",
          }}>
            {filteredTrails.length} trilha{filteredTrails.length !== 1 ? 's' : ''}
          </span>
        )}

        <div style={{
          marginLeft: 'auto', display: 'flex',
          background: 'rgba(255,255,255,0.03)',
          border: '1px solid rgba(255,255,255,0.07)',
          borderRadius: 8, overflow: 'hidden',
        }}>
          {[
            { mode: 'trails', label: 'Trilhas' },
            { mode: 'courses', label: 'Cursos' },
          ].map(({ mode, label }, i) => (
            <button
              key={mode}
              type="button"
              onClick={() => setViewMode(mode)}
              style={{
                padding: '0.28rem 0.85rem',
                border: 'none',
                borderRight: i === 0 ? '1px solid rgba(255,255,255,0.06)' : 'none',
                background: viewMode === mode ? 'rgba(99,102,241,0.18)' : 'transparent',
                color: viewMode === mode ? '#818cf8' : '#3a3a5a',
                fontSize: '0.75rem',
                fontWeight: viewMode === mode ? 600 : 400,
                fontFamily: "'Inter', sans-serif",
                cursor: 'pointer',
                transition: 'all 0.15s ease',
              }}
            >
              {label}
            </button>
          ))}
        </div>
      </div>
      
      {/* Trails List */}
      {viewMode === "trails" ? (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
          {filteredTrails.length === 0 ? (
            <div style={{
              background: '#12121a',
              border: '1px solid rgba(255,255,255,0.07)',
              borderRadius: 12, padding: '2.5rem 1.5rem', textAlign: 'center',
            }}>
              <div style={{
                width: 44, height: 44, borderRadius: 11,
                background: 'rgba(255,255,255,0.04)',
                border: '1px solid rgba(255,255,255,0.07)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                margin: '0 auto 0.85rem',
              }}>
                <Route size={20} style={{ color: '#3a3a5a' }} />
              </div>
              <h3 style={{
                fontFamily: "'Space Grotesk', sans-serif",
                fontSize: '0.92rem', fontWeight: 600, color: '#6a6a8a', marginBottom: '0.3rem',
              }}>
                {selectedSector === "all"
                  ? "Nenhuma trilha criada"
                  : `Nenhuma trilha para ${getSectorDisplayName(selectedSector)}`}
              </h3>
              <p style={{ fontSize: '0.77rem', color: '#3a3a5a', marginBottom: '1.1rem', fontFamily: "'Inter', sans-serif" }}>
                Crie trilhas de aprendizado para organizar cursos em sequências
              </p>
              <button
                onClick={() => setDialogOpen(true)}
                style={{
                  display: 'inline-flex', alignItems: 'center', gap: '0.4rem',
                  padding: '0.45rem 1rem', borderRadius: 8,
                  background: 'linear-gradient(135deg, #E67E22, #CA6F1E)',
                  border: 'none', color: '#fff',
                  fontSize: '0.79rem', fontWeight: 600, cursor: 'pointer',
                  fontFamily: "'Inter', sans-serif",
                }}
              >
                <Plus size={13} />
                Criar Primeira Trilha
              </button>
            </div>
          ) : (
            filteredTrails.map(trail => {
              const trailCourses = trail.course_ids.map(id => getCourseById(id)).filter(Boolean);
              const totalDuration = trailCourses.reduce((sum, c) => sum + (c.total_duration_minutes || 0), 0);
              const hasCourseLevelAssessments = trailCourses.some(c => c.requires_assessment);
              const requiresFinalAssessment = Boolean(trail.requires_assessment && trail.linked_assessment_id);
              const finalAssessment = assessments.find(a => a.id === trail.linked_assessment_id);
              const trailSectorNames = (trail.sectors || (trail.sector ? [trail.sector] : []))
                .map(getSectorDisplayName)
                .join(", ");

              return (
                <div key={trail.id} style={{
                  background: '#12121a',
                  border: '1px solid rgba(255,255,255,0.07)',
                  borderRadius: 11,
                  overflow: 'hidden',
                }}>
                <details className="group">
                  <summary style={{
                    display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between',
                    cursor: 'pointer', padding: '0.75rem 0.875rem', gap: '0.75rem',
                    listStyle: 'none',
                  }}>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      {/* Title row */}
                      <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.25rem', flexWrap: 'wrap' }}>
                        <div style={{
                          width: 26, height: 26, borderRadius: 6,
                          background: 'rgba(59,130,246,0.1)',
                          border: '1px solid rgba(59,130,246,0.15)',
                          display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
                        }}>
                          <Route size={12} style={{ color: '#3b82f6' }} />
                        </div>
                        <span style={{
                          fontFamily: "'Space Grotesk', sans-serif",
                          fontSize: '0.9rem', fontWeight: 700, color: '#e8e8f0',
                        }}>{trail.name}</span>
                        {trailSectorNames && (
                          <span style={{
                            display: 'inline-flex', alignItems: 'center',
                            padding: '1px 6px', borderRadius: 4,
                            background: 'rgba(99,102,241,0.1)',
                            border: '1px solid rgba(99,102,241,0.18)',
                            fontSize: '0.65rem', fontWeight: 600, color: '#818cf8',
                            fontFamily: "'Inter', sans-serif",
                          }}>
                            {trailSectorNames}
                          </span>
                        )}
                      </div>
                      {/* Description */}
                      <p style={{
                        fontSize: '0.75rem', color: '#4a4a6a',
                        fontFamily: "'Inter', sans-serif", margin: '0 0 0.45rem',
                        paddingLeft: '2.1rem', lineHeight: 1.5,
                        overflow: 'hidden', display: '-webkit-box',
                        WebkitLineClamp: 2, WebkitBoxOrient: 'vertical',
                      }}>{trail.description}</p>
                      {/* Meta chips */}
                      <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', paddingLeft: '2.1rem', flexWrap: 'wrap' }}>
                        <span style={{ display: 'flex', alignItems: 'center', gap: '0.25rem', fontSize: '0.7rem', color: '#4a4a6a', fontFamily: "'Inter', sans-serif" }}>
                          <BookOpen size={10} style={{ color: '#3a3a5a' }} />
                          {trailCourses.length} curso{trailCourses.length !== 1 ? 's' : ''}
                        </span>
                        <span style={{ display: 'flex', alignItems: 'center', gap: '0.25rem', fontSize: '0.7rem', color: '#4a4a6a', fontFamily: "'Inter', sans-serif" }}>
                          <Clock size={10} style={{ color: '#3a3a5a' }} />
                          {totalDuration} min
                        </span>
                        {hasCourseLevelAssessments && (
                          <span style={{ display: 'flex', alignItems: 'center', gap: '0.25rem', fontSize: '0.7rem', color: '#6366f1', fontFamily: "'Inter', sans-serif" }}>
                            <Target size={10} />
                            Avaliações em cursos
                          </span>
                        )}
                        {requiresFinalAssessment && (
                          <span style={{ display: 'flex', alignItems: 'center', gap: '0.25rem', fontSize: '0.7rem', color: '#22c55e', fontFamily: "'Inter', sans-serif" }}>
                            <Target size={10} />
                            Avaliação final
                          </span>
                        )}
                      </div>
                    </div>

                    {/* Actions */}
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.3rem', flexShrink: 0 }}>
                      <button
                        onClick={(e) => { e.preventDefault(); e.stopPropagation(); handleEditTrail(trail); }}
                        type="button"
                        title="Editar trilha"
                        data-testid={`edit-trail-${trail.id}`}
                        style={{
                          display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                          width: 26, height: 26, borderRadius: 6,
                          background: 'rgba(255,255,255,0.04)',
                          border: '1px solid rgba(255,255,255,0.08)',
                          color: '#5a5a7a', cursor: 'pointer',
                        }}
                      >
                        <Edit size={11} />
                      </button>
                      <button
                        onClick={(e) => { e.preventDefault(); e.stopPropagation(); openCoursePicker(trail); }}
                        type="button"
                        style={{
                          display: 'inline-flex', alignItems: 'center', gap: '0.25rem',
                          padding: '0.22rem 0.6rem', borderRadius: 6,
                          background: 'rgba(99,102,241,0.08)',
                          border: '1px solid rgba(99,102,241,0.15)',
                          color: '#818cf8', cursor: 'pointer',
                          fontSize: '0.7rem', fontWeight: 500,
                          fontFamily: "'Inter', sans-serif",
                        }}
                      >
                        <Plus size={11} />
                        Adicionar curso
                      </button>
                      <button
                        onClick={(e) => { e.preventDefault(); e.stopPropagation(); openCreateCourseModal(trail); }}
                        type="button"
                        style={{
                          display: 'inline-flex', alignItems: 'center', gap: '0.25rem',
                          padding: '0.22rem 0.6rem', borderRadius: 6,
                          background: 'rgba(99,102,241,0.08)',
                          border: '1px solid rgba(99,102,241,0.15)',
                          color: '#818cf8', cursor: 'pointer',
                          fontSize: '0.7rem', fontWeight: 500,
                          fontFamily: "'Inter', sans-serif",
                        }}
                      >
                        <Plus size={11} />
                        Criar curso
                      </button>
                      <button
                        onClick={(e) => { e.preventDefault(); e.stopPropagation(); handleDeleteTrail(trail.id, trail.name); }}
                        type="button"
                        title="Excluir trilha"
                        data-testid={`delete-trail-${trail.id}`}
                        style={{
                          display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                          width: 26, height: 26, borderRadius: 6,
                          background: 'rgba(239,68,68,0.06)',
                          border: '1px solid rgba(239,68,68,0.12)',
                          color: '#f87171', cursor: 'pointer',
                        }}
                      >
                        <Trash2 size={11} />
                      </button>
                      <ChevronDown size={14} style={{ color: '#3a3a5a', transition: 'transform 0.2s', flexShrink: 0 }} className="group-open:rotate-180" />
                    </div>
                  </summary>

                  <div style={{ borderTop: '1px solid rgba(255,255,255,0.05)', padding: '0.75rem 0.875rem 0.875rem' }}>
                      {/* Course Sequence (expanded) */}
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                        <h4 style={{
                          display: 'flex', alignItems: 'center', gap: '0.4rem',
                          fontSize: '0.72rem', fontWeight: 700, color: '#3a3a5a',
                          textTransform: 'uppercase', letterSpacing: '0.09em',
                          fontFamily: "'Inter', sans-serif", margin: 0,
                        }}>
                          <MapPin size={11} style={{ color: '#3a3a5a' }} />
                          Sequência de Cursos
                        </h4>

                        <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                          {trailCourses.map((course, index) => {
                            const coursePosition = trail.course_ids.indexOf(course.id);
                            const isFirstCourse = coursePosition <= 0;
                            const isLastCourse = coursePosition === trail.course_ids.length - 1 || coursePosition === -1;

                            return (
                              <div
                                key={`${trail.id}-${course.id}`}
                                style={{ display: 'flex', alignItems: 'center', gap: '0.6rem' }}
                              >
                                {/* Step number */}
                                <div style={{
                                  width: 22, height: 22, borderRadius: '50%', flexShrink: 0,
                                  background: 'rgba(59,130,246,0.15)',
                                  border: '1px solid rgba(59,130,246,0.2)',
                                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                                  fontSize: '0.6rem', fontWeight: 700, color: '#60a5fa',
                                  fontFamily: 'monospace',
                                }}>
                                  {index + 1}
                                </div>

                                <div style={{
                                  flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                                  gap: '0.5rem',
                                  background: 'rgba(255,255,255,0.03)',
                                  border: '1px solid rgba(255,255,255,0.05)',
                                  borderRadius: 8, padding: '0.45rem 0.65rem',
                                  minWidth: 0,
                                }}>
                                  <div style={{ flex: 1, minWidth: 0 }}>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', flexWrap: 'wrap' }}>
                                      <span style={{
                                        fontSize: '0.8rem', fontWeight: 600, color: '#c8c8d8',
                                        fontFamily: "'Inter', sans-serif",
                                        overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                                      }} title={course.title}>{course.title}</span>
                                      {course.requires_assessment && (
                                        <span style={{
                                          display: 'inline-flex', alignItems: 'center', gap: '0.2rem',
                                          padding: '1px 5px', borderRadius: 4,
                                          background: 'rgba(34,197,94,0.1)',
                                          border: '1px solid rgba(34,197,94,0.15)',
                                          fontSize: '0.62rem', color: '#4ade80',
                                          fontFamily: "'Inter', sans-serif",
                                        }}>
                                          <Target size={8} />
                                          {course.passing_score_required}% mín.
                                        </span>
                                      )}
                                      {index > 0 && (
                                        <span style={{
                                          display: 'inline-flex', alignItems: 'center', gap: '0.2rem',
                                          padding: '1px 5px', borderRadius: 4,
                                          background: 'rgba(255,255,255,0.04)',
                                          border: '1px solid rgba(255,255,255,0.07)',
                                          fontSize: '0.62rem', color: '#3a3a5a',
                                          fontFamily: "'Inter', sans-serif",
                                        }}>
                                          <Lock size={8} />
                                          Bloqueado
                                        </span>
                                      )}
                                    </div>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginTop: '0.1rem' }}>
                                      <span style={{ fontSize: '0.68rem', color: '#3a3a5a', fontFamily: 'monospace' }}>
                                        {course.segments?.length || 0} mód. · {course.total_duration_minutes} min
                                      </span>
                                      {getOtherTrailsWithCourse(course.id, trail.id).length > 0 && (
                                        <span style={{ fontSize: '0.68rem', color: '#ca8a04', fontFamily: "'Inter', sans-serif" }}>
                                          +{getOtherTrailsWithCourse(course.id, trail.id).length} trilha(s)
                                        </span>
                                      )}
                                    </div>
                                  </div>

                                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.25rem', flexShrink: 0 }}>
                                    <button
                                      onClick={() => handleSequenceMove(trail, course.id, "up")}
                                      disabled={isFirstCourse}
                                      title="Mover para cima"
                                      style={{
                                        width: 22, height: 22, borderRadius: 5, display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                                        background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.07)',
                                        color: isFirstCourse ? '#2a2a4a' : '#5a5a7a', cursor: isFirstCourse ? 'not-allowed' : 'pointer',
                                      }}
                                    >
                                      <MoveUp size={10} />
                                    </button>
                                    <button
                                      onClick={() => handleSequenceMove(trail, course.id, "down")}
                                      disabled={isLastCourse}
                                      title="Mover para baixo"
                                      style={{
                                        width: 22, height: 22, borderRadius: 5, display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                                        background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.07)',
                                        color: isLastCourse ? '#2a2a4a' : '#5a5a7a', cursor: isLastCourse ? 'not-allowed' : 'pointer',
                                      }}
                                    >
                                      <MoveDown size={10} />
                                    </button>
                                    <button
                                      onClick={() => onEditCourse?.(course)}
                                      data-testid={`edit-course-in-trail-${course.id}`}
                                      title="Editar curso"
                                      style={{
                                        width: 22, height: 22, borderRadius: 5, display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                                        background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.07)',
                                        color: '#5a5a7a', cursor: 'pointer',
                                      }}
                                    >
                                      <Edit size={10} />
                                    </button>
                                    <button
                                      onClick={() => handleTrailCourseDelete(course.id, trail.id)}
                                      data-testid={`delete-course-in-trail-${course.id}`}
                                      title="Remover da trilha"
                                      style={{
                                        width: 22, height: 22, borderRadius: 5, display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                                        background: 'rgba(239,68,68,0.06)', border: '1px solid rgba(239,68,68,0.1)',
                                        color: '#f87171', cursor: 'pointer',
                                      }}
                                    >
                                      <Trash2 size={10} />
                                    </button>
                                    <button
                                      onClick={() => handleDeleteCourseFromSystem(course.id, trail.id)}
                                      data-testid={`delete-course-system-${course.id}`}
                                      title="Excluir do sistema"
                                      style={{
                                        display: 'inline-flex', alignItems: 'center', gap: '0.2rem',
                                        padding: '0.18rem 0.45rem', borderRadius: 5, height: 22,
                                        background: 'rgba(239,68,68,0.06)', border: '1px solid rgba(239,68,68,0.1)',
                                        color: '#f87171', cursor: 'pointer',
                                        fontSize: '0.62rem', fontFamily: "'Inter', sans-serif",
                                      }}
                                    >
                                      <Trash2 size={9} />
                                      Sistema
                                    </button>
                                  </div>
                                </div>
                              </div>
                            );
                          })}
                          {requiresFinalAssessment && finalAssessment && (
                            <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem' }}>
                              <div style={{
                                width: 22, height: 22, borderRadius: '50%', flexShrink: 0,
                                background: 'rgba(168,85,247,0.15)',
                                border: '1px solid rgba(168,85,247,0.2)',
                                display: 'flex', alignItems: 'center', justifyContent: 'center',
                                fontSize: '0.6rem', fontWeight: 700, color: '#c084fc',
                                fontFamily: 'monospace',
                              }}>
                                {trailCourses.length + 1}
                              </div>
                              <div style={{
                                flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                                gap: '0.5rem',
                                background: 'rgba(168,85,247,0.06)',
                                border: '1px solid rgba(168,85,247,0.15)',
                                borderRadius: 8, padding: '0.45rem 0.65rem',
                              }}>
                                <div>
                                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.3rem', marginBottom: '0.1rem' }}>
                                    <Target size={10} style={{ color: '#c084fc' }} />
                                    <span style={{ fontSize: '0.62rem', fontWeight: 700, color: '#c084fc', textTransform: 'uppercase', letterSpacing: '0.08em', fontFamily: "'Inter', sans-serif" }}>Avaliação Final</span>
                                  </div>
                                  <span style={{ fontSize: '0.8rem', fontWeight: 600, color: '#c8c8d8', fontFamily: "'Inter', sans-serif" }}>{finalAssessment.title}</span>
                                </div>
                                <button
                                  onClick={() => handleRemoveFinalAssessment(trail)}
                                  style={{
                                    display: 'inline-flex', alignItems: 'center', gap: '0.25rem',
                                    padding: '0.22rem 0.55rem', borderRadius: 5,
                                    background: 'rgba(239,68,68,0.06)', border: '1px solid rgba(239,68,68,0.1)',
                                    color: '#f87171', cursor: 'pointer',
                                    fontSize: '0.68rem', fontFamily: "'Inter', sans-serif",
                                  }}
                                >
                                  Remover
                                </button>
                              </div>
                            </div>
                          )}
                          {(!requiresFinalAssessment || !finalAssessment) && (
                            <button
                              onClick={() => openFinalAssessmentPicker(trail)}
                              style={{
                                display: 'inline-flex', alignItems: 'center', gap: '0.35rem',
                                padding: '0.35rem 0.8rem', borderRadius: 7,
                                background: 'rgba(99,102,241,0.07)',
                                border: '1px dashed rgba(99,102,241,0.2)',
                                color: '#6366f1', cursor: 'pointer',
                                fontSize: '0.72rem', fontWeight: 500,
                                fontFamily: "'Inter', sans-serif",
                                alignSelf: 'flex-start',
                              }}
                            >
                              <Target size={11} />
                              Adicionar avaliação final
                            </button>
                          )}
                        </div>
                      </div>

                      <div style={{
                        marginTop: '0.6rem', paddingTop: '0.6rem',
                        borderTop: '1px solid rgba(255,255,255,0.04)',
                        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                      }}>
                        <span style={{ display: 'flex', alignItems: 'center', gap: '0.3rem', fontSize: '0.68rem', color: '#4a4a2a', fontFamily: "'Inter', sans-serif" }}>
                          <Award size={11} style={{ color: '#ca8a04' }} />
                          Conclusão desbloqueia certificado
                        </span>
                        <span style={{ fontSize: '0.65rem', color: '#3a3a5a', fontFamily: 'monospace' }}>
                          Criada em {new Date(trail.created_at).toLocaleDateString('pt-BR')}
                        </span>
                      </div>
                    </div>
                </details>
              </div>
              );
            })
          )}
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
          {courses
            .filter(course => selectedSector === "all" || course.sectors?.includes(selectedSector))
            .map(course => {
              const linkedTrailNames = trails.filter(t => t.course_ids?.includes(course.id)).map(t => t.name);
              return (
                <div key={course.id} style={{
                  display: 'flex', alignItems: 'center', gap: '0.65rem',
                  padding: '0.55rem 0.75rem',
                  background: '#12121a',
                  border: '1px solid rgba(255,255,255,0.06)',
                  borderRadius: 9,
                }}>
                  <div style={{
                    width: 28, height: 28, borderRadius: 7, flexShrink: 0,
                    background: 'rgba(34,197,94,0.08)',
                    border: '1px solid rgba(34,197,94,0.12)',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                  }}>
                    <BookOpen size={12} style={{ color: '#22c55e' }} />
                  </div>

                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.45rem', marginBottom: '0.1rem', flexWrap: 'wrap' }}>
                      <span style={{
                        fontFamily: "'Inter', sans-serif", fontSize: '0.82rem', fontWeight: 600,
                        color: '#d8d8e8', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                      }} title={course.title}>{course.title}</span>
                      {(course.sectors || []).map(s => (
                        <span key={s} style={{
                          padding: '1px 5px', borderRadius: 4,
                          background: 'rgba(255,255,255,0.04)',
                          border: '1px solid rgba(255,255,255,0.07)',
                          fontSize: '0.62rem', color: '#4a4a6a',
                          fontFamily: "'Inter', sans-serif",
                        }}>{getSectorDisplayName(s)}</span>
                      ))}
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem' }}>
                      <span style={{ fontSize: '0.68rem', color: '#3a3a5a', fontFamily: 'monospace' }}>
                        {course.segments?.length || 0} mód. · {course.total_duration_minutes} min
                      </span>
                      {linkedTrailNames.length > 0 ? (
                        <span style={{ fontSize: '0.68rem', color: '#3a3a5a', fontFamily: "'Inter', sans-serif", overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {linkedTrailNames.join(", ")}
                        </span>
                      ) : (
                        <span style={{ fontSize: '0.68rem', color: '#2a2a4a', fontFamily: "'Inter', sans-serif" }}>Sem trilha</span>
                      )}
                    </div>
                  </div>

                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.25rem', flexShrink: 0 }}>
                    <button
                      onClick={() => onEditCourse?.(course)}
                      data-testid={`edit-course-${course.id}`}
                      title="Editar"
                      style={{
                        width: 26, height: 26, borderRadius: 6,
                        display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                        background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.08)',
                        color: '#5a5a7a', cursor: 'pointer',
                      }}
                    >
                      <Edit size={11} />
                    </button>
                    <button
                      onClick={() => onDeleteCourse?.(course.id, course.title)}
                      data-testid={`delete-course-${course.id}`}
                      title="Excluir"
                      style={{
                        width: 26, height: 26, borderRadius: 6,
                        display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                        background: 'rgba(239,68,68,0.06)', border: '1px solid rgba(239,68,68,0.1)',
                        color: '#f87171', cursor: 'pointer',
                      }}
                    >
                      <Trash2 size={11} />
                    </button>
                  </div>
                </div>
              );
            })}
        </div>
      )}

      <Dialog
        modal={!showVideoUploadManager}
        open={createCourseModal.open}
        onOpenChange={(open) => {
          if (!open) {
            if (showVideoUploadManager) return;
            closeCreateCourseModal();
            resetCourseForm();
          }
        }}
      >
        <DialogContent
          className="max-w-2xl max-h-[86vh] overflow-y-auto"
          style={{ background: '#12121a', border: '1px solid rgba(255,255,255,0.08)', color: '#e0e0f0' }}
          onEscapeKeyDown={(event) => {
            if (showVideoUploadManager) event.preventDefault();
          }}
          onInteractOutside={(event) => {
            if (showVideoUploadManager) event.preventDefault();
          }}
          onPointerDownOutside={(event) => {
            if (showVideoUploadManager) event.preventDefault();
          }}
        >
          <DialogHeader style={{ paddingBottom: '0.75rem', borderBottom: '1px solid rgba(255,255,255,0.06)', marginBottom: '0.5rem' }}>
            <DialogTitle style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', fontFamily: "'Space Grotesk', sans-serif", fontSize: '0.96rem', fontWeight: 700, color: '#e8e8f0' }}>
              <div style={{ width: 26, height: 26, borderRadius: 7, flexShrink: 0, background: 'rgba(59,130,246,0.1)', border: '1px solid rgba(59,130,246,0.2)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <BookOpen size={13} style={{ color: '#60a5fa' }} />
              </div>
              Criar Novo Curso
            </DialogTitle>
            <DialogDescription style={{ fontSize: '0.73rem', color: '#4a4a6a', fontFamily: "'Inter', sans-serif", paddingLeft: '2.35rem', marginTop: '0.1rem' }}>
              Preencha as informações do curso e adicione os módulos
            </DialogDescription>
          </DialogHeader>

          <form onSubmit={handleCreateCourseForTrail} style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
            <div className="form-group">
              <Label htmlFor="trail-course-title" className="form-label">
                Título do Curso *
              </Label>
              <Input
                id="trail-course-title"
                value={courseForm.title}
                onChange={(e) => setCourseForm(prev => ({ ...prev, title: e.target.value }))}
                required
                data-testid="trail-course-title-input"
                className="dialog-input"
              />
            </div>

            <div className="form-group">
              <Label htmlFor="trail-course-thumbnail" className="form-label">
                Imagem de Capa
              </Label>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                {courseForm.thumbnail_url && (
                  <div style={{ position: 'relative' }}>
                    <img
                      src={courseForm.thumbnail_url}
                      alt="Preview da capa"
                      style={{ width: '100%', height: '7rem', objectFit: 'cover', borderRadius: 8, border: '1px solid rgba(255,255,255,0.08)' }}
                    />
                    <button
                      type="button"
                      onClick={() => setCourseForm(prev => ({ ...prev, thumbnail_url: "" }))}
                      style={{ position: 'absolute', top: '0.4rem', right: '0.4rem', width: 24, height: 24, borderRadius: 5, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'rgba(239,68,68,0.15)', border: '1px solid rgba(239,68,68,0.3)', color: '#f87171', cursor: 'pointer' }}
                    >
                      <Trash2 size={11} />
                    </button>
                  </div>
                )}
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                  <input
                    type="file"
                    accept="image/*"
                    onChange={(e) => {
                      const file = e.target.files[0];
                      if (file) handleImageUpload(file);
                    }}
                    className="hidden"
                    id="trail-course-thumbnail-input"
                    data-testid="trail-course-thumbnail-input"
                  />
                  <label
                    htmlFor="trail-course-thumbnail-input"
                    style={{ display: 'inline-flex', alignItems: 'center', gap: '0.4rem', padding: '0.35rem 0.75rem', borderRadius: 7, background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.09)', color: '#8a8ab0', fontSize: '0.78rem', fontFamily: "'Inter', sans-serif", cursor: 'pointer' }}
                  >
                    {imageUploading ? (
                      <>
                        <div className="loading-spinner" style={{ width: 12, height: 12 }} />
                        Enviando...
                      </>
                    ) : (
                      <>
                        <Plus size={12} />
                        Selecionar Imagem
                      </>
                    )}
                  </label>
                  {courseForm.thumbnail_url && (
                    <span style={{ fontSize: '0.72rem', color: '#34d399', fontFamily: "'Inter', sans-serif" }}>✓ Imagem carregada</span>
                  )}
                </div>
                <p style={{ fontSize: '0.68rem', color: '#3a3a5a', fontFamily: "'Inter', sans-serif", margin: 0 }}>
                  Formatos aceitos: JPG, PNG, WebP. Tamanho recomendado: 400x200px
                </p>
              </div>
            </div>

            <div className="form-group">
              <Label htmlFor="trail-course-description" className="form-label">
                Descrição *
              </Label>
              <Textarea
                id="trail-course-description"
                value={courseForm.description}
                onChange={(e) => setCourseForm(prev => ({ ...prev, description: e.target.value }))}
                required
                rows={3}
                data-testid="trail-course-description-input"
                className="dialog-textarea"
              />
            </div>

            <div style={{ border: '1px solid rgba(245,158,11,0.15)', borderRadius: 9, padding: '0.75rem', background: 'rgba(245,158,11,0.03)' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.6rem' }}>
                <input
                  type="checkbox"
                  id="trail-course-requires-assessment"
                  checked={courseForm.requires_assessment}
                  onChange={(e) => setCourseForm(prev => ({ ...prev, requires_assessment: e.target.checked }))}
                  style={{ width: 14, height: 14, accentColor: '#f59e0b' }}
                  data-testid="trail-course-requires-assessment-checkbox"
                />
                <label htmlFor="trail-course-requires-assessment" style={{ fontSize: '0.8rem', fontWeight: 600, color: '#c0c0d8', fontFamily: "'Inter', sans-serif", cursor: 'pointer' }}>
                  Ativar Avaliação
                </label>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem', opacity: courseForm.requires_assessment ? 1 : 0.4 }}>
                <div className="form-group">
                  <Label htmlFor="trail-course-ai-num-questions" className="form-label">
                    Nº de Questões
                  </Label>
                  <Input
                    id="trail-course-ai-num-questions"
                    type="number"
                    min="1"
                    max="25"
                    value={courseAssessmentAIConfig.num_questions}
                    onChange={(e) => setCourseAssessmentAIConfig({ num_questions: e.target.value })}
                    disabled={!courseForm.requires_assessment}
                    className="dialog-input"
                  />
                </div>
                <div className="form-group">
                  <Label htmlFor="trail-course-passing-score" className="form-label">
                    Nota Mínima (%)
                  </Label>
                  <Input
                    id="trail-course-passing-score"
                    type="number"
                    min="0"
                    max="100"
                    step="0.1"
                    value={courseForm.passing_score_required}
                    onChange={(e) => setCourseForm(prev => ({ ...prev, passing_score_required: parseFloat(e.target.value) || 70.0 }))}
                    disabled={!courseForm.requires_assessment}
                    data-testid="trail-course-passing-score-input"
                    className="dialog-input"
                  />
                </div>
              </div>
            </div>

            <div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', marginBottom: '0.5rem' }}>
                <div style={{ width: 20, height: 20, borderRadius: 5, background: 'rgba(59,130,246,0.1)', border: '1px solid rgba(59,130,246,0.2)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <BookOpen size={10} style={{ color: '#60a5fa' }} />
                </div>
                <span style={{ fontSize: '0.7rem', fontWeight: 700, color: '#6a6a9a', fontFamily: "'Inter', sans-serif", textTransform: 'uppercase', letterSpacing: '0.07em' }}>Módulos do Curso</span>
              </div>

              <DragDropContext onDragEnd={handleCourseSegmentDragEnd}>
                <StrictModeDroppable droppableId="trail-course-segments" direction="vertical">
                  {(provided) => (
                    <div {...provided.droppableProps} ref={provided.innerRef} style={{ display: 'flex', flexDirection: 'column', gap: '0.35rem', marginBottom: '0.5rem' }}>
                      {courseForm.segments.map((segment, index) => (
                        <Draggable key={segment.id} draggableId={String(segment.id)} index={index}>
                          {(provided, snapshot) => (
                            <div
                              ref={provided.innerRef}
                              {...provided.draggableProps}
                              {...provided.dragHandleProps}
                              style={{ ...provided.draggableProps.style, background: snapshot.isDragging ? 'rgba(59,130,246,0.08)' : 'rgba(255,255,255,0.03)', border: snapshot.isDragging ? '1px solid rgba(59,130,246,0.25)' : '1px solid rgba(255,255,255,0.07)', borderRadius: 7, padding: '0.4rem 0.6rem', display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'grab' }}
                            >
                              <GripVertical size={13} style={{ color: '#3a3a5a', flexShrink: 0 }} />
                              <div style={{ flex: 1, minWidth: 0 }}>
                                <span style={{ fontSize: '0.78rem', fontWeight: 600, color: '#c0c0d8', fontFamily: "'Inter', sans-serif" }}>{segment.title}</span>
                                <span style={{ fontSize: '0.65rem', color: '#4a4a6a', fontFamily: 'monospace', marginLeft: '0.4rem' }}>{segment.duration_minutes}min</span>
                              </div>
                              <button
                                type="button"
                                onClick={() => removeSegment(index)}
                                data-testid={`trail-remove-segment-${index}`}
                                style={{ width: 22, height: 22, borderRadius: 5, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'rgba(239,68,68,0.06)', border: '1px solid rgba(239,68,68,0.12)', color: '#f87171', cursor: 'pointer', flexShrink: 0 }}
                              >
                                <Trash2 size={11} />
                              </button>
                            </div>
                          )}
                        </Draggable>
                      ))}
                      {provided.placeholder}
                    </div>
                  )}
                </StrictModeDroppable>
              </DragDropContext>

              <div style={{ border: '1px dashed rgba(59,130,246,0.2)', borderRadius: 9, padding: '0.75rem', background: 'rgba(59,130,246,0.02)', display: 'flex', flexDirection: 'column', gap: '0.6rem' }}>
                <span style={{ fontSize: '0.7rem', fontWeight: 700, color: '#6a6a9a', fontFamily: "'Inter', sans-serif", textTransform: 'uppercase', letterSpacing: '0.07em' }}>Adicionar Módulo</span>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr auto', gap: '0.5rem' }}>
                  <Input
                    placeholder="Título do módulo"
                    value={newSegment.title}
                    onChange={(e) => setNewSegment(prev => ({ ...prev, title: e.target.value }))}
                    data-testid="trail-segment-title-input"
                    className="dialog-input"
                  />
                  <Input
                    type="number"
                    placeholder="Min"
                    value={newSegment.duration_minutes}
                    onChange={(e) => setNewSegment(prev => ({ ...prev, duration_minutes: parseInt(e.target.value) || 0 }))}
                    data-testid="trail-segment-duration-input"
                    className="dialog-input"
                    style={{ width: '5rem' }}
                  />
                </div>

                <div>
                  <span style={{ fontSize: '0.7rem', fontWeight: 600, color: '#5a5a8a', fontFamily: "'Inter', sans-serif", display: 'block', marginBottom: '0.35rem' }}>Vídeo</span>
                  <div style={{ display: 'flex', gap: '0.5rem' }}>
                    <button
                      type="button"
                      onClick={() => setShowVideoUploadManager(true)}
                      style={{ flex: 1, padding: '0.35rem 0.6rem', borderRadius: 7, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.35rem', background: newSegment.video_id ? 'rgba(59,130,246,0.1)' : 'rgba(255,255,255,0.04)', border: newSegment.video_id ? '1px solid rgba(59,130,246,0.25)' : '1px solid rgba(255,255,255,0.09)', color: newSegment.video_id ? '#60a5fa' : '#6a6a9a', fontSize: '0.75rem', fontFamily: "'Inter', sans-serif", cursor: 'pointer' }}
                    >
                      <Upload size={12} />
                      {newSegment.video_id ? "Vídeo Enviado" : "Upload de Vídeo"}
                    </button>
                    <Input
                      placeholder="Ou cole URL do YouTube"
                      value={newSegment.video_url}
                      onChange={(e) => {
                        setNewSegment(prev => ({
                          ...prev,
                          video_url: e.target.value,
                          video_type: e.target.value ? "youtube" : "local"
                        }));
                      }}
                      data-testid="trail-segment-url-input"
                      className="dialog-input"
                      style={{ flex: 1 }}
                    />
                  </div>
                  {newSegment.video_id && (
                    <p style={{ fontSize: '0.68rem', color: '#34d399', fontFamily: "'Inter', sans-serif", marginTop: '0.2rem' }}>✓ Vídeo selecionado (ID: {newSegment.video_id.substring(0, 8)}...)</p>
                  )}
                </div>

                <Textarea
                  placeholder="Descrição do módulo (opcional)"
                  value={newSegment.description}
                  onChange={(e) => setNewSegment(prev => ({ ...prev, description: e.target.value }))}
                  rows={2}
                  data-testid="trail-segment-description-input"
                  className="dialog-textarea"
                />
                <button
                  type="button"
                  onClick={addSegment}
                  data-testid="trail-add-segment-btn"
                  style={{ alignSelf: 'flex-start', display: 'inline-flex', alignItems: 'center', gap: '0.35rem', padding: '0.35rem 0.75rem', borderRadius: 7, background: 'rgba(59,130,246,0.07)', border: '1px solid rgba(59,130,246,0.18)', color: '#60a5fa', fontSize: '0.75rem', fontFamily: "'Inter', sans-serif", cursor: 'pointer' }}
                >
                  <Plus size={12} />
                  Adicionar Módulo
                </button>
              </div>
            </div>

            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '0.5rem', paddingTop: '0.875rem', borderTop: '1px solid rgba(255,255,255,0.06)', marginTop: '0.25rem' }}>
              <button
                type="button"
                onClick={() => { closeCreateCourseModal(); resetCourseForm(); }}
                data-testid="trail-cancel-course-btn"
                style={{ padding: '0.4rem 0.9rem', borderRadius: 7, background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.09)', color: '#6a6a9a', fontSize: '0.8rem', fontFamily: "'Inter', sans-serif", cursor: 'pointer' }}
              >
                Cancelar
              </button>
              <button
                type="submit"
                data-testid="trail-save-course-btn"
                style={{ padding: '0.4rem 0.9rem', borderRadius: 7, background: 'rgba(59,130,246,0.15)', border: '1px solid rgba(59,130,246,0.3)', color: '#60a5fa', fontSize: '0.8rem', fontFamily: "'Space Grotesk', sans-serif", fontWeight: 600, cursor: 'pointer' }}
              >
                Criar Curso
              </button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      <Dialog open={coursePickerModal.open} onOpenChange={(open) => { if (!open) closeCoursePicker(); }}>
        <DialogContent className="max-w-3xl max-h-[80vh] overflow-y-auto" style={{ background: '#12121a', border: '1px solid rgba(255,255,255,0.08)', color: '#e0e0f0' }}>
          <DialogHeader style={{ paddingBottom: '0.75rem', borderBottom: '1px solid rgba(255,255,255,0.06)', marginBottom: '0.5rem' }}>
            <DialogTitle style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', fontFamily: "'Space Grotesk', sans-serif", fontSize: '0.96rem', fontWeight: 700, color: '#e8e8f0' }}>
              <div style={{ width: 26, height: 26, borderRadius: 7, flexShrink: 0, background: 'rgba(59,130,246,0.1)', border: '1px solid rgba(59,130,246,0.2)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <BookOpen size={13} style={{ color: '#60a5fa' }} />
              </div>
              Adicionar Curso
            </DialogTitle>
            <DialogDescription style={{ fontSize: '0.73rem', color: '#4a4a6a', fontFamily: "'Inter', sans-serif", paddingLeft: '2.35rem', marginTop: '0.1rem' }}>
              Selecione um curso disponível para adicionar à trilha <span style={{ color: '#8a8ab0' }}>{coursePickerModal.trail?.name}</span>
            </DialogDescription>
          </DialogHeader>

          {coursePickerModal.trail ? (
            availableCoursesForPicker.length === 0 ? (
              <div style={{ textAlign: 'center', padding: '2.5rem 0', fontSize: '0.82rem', color: '#4a4a6a', fontFamily: "'Inter', sans-serif" }}>
                Todos os cursos já fazem parte desta trilha.
              </div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.4rem' }}>
                {availableCoursesForPicker.map(course => (
                  <div key={course.id} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '1rem', padding: '0.65rem 0.75rem', border: '1px solid rgba(255,255,255,0.07)', borderRadius: 8, background: 'rgba(255,255,255,0.02)' }}>
                    <div style={{ minWidth: 0 }}>
                      <h4 style={{ fontSize: '0.82rem', fontWeight: 600, color: '#c0c0d8', fontFamily: "'Inter', sans-serif", margin: 0 }}>{course.title}</h4>
                      <p style={{ fontSize: '0.72rem', color: '#5a5a8a', fontFamily: "'Inter', sans-serif", margin: '0.15rem 0 0', overflow: 'hidden', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical' }}>{course.description}</p>
                      <div style={{ fontSize: '0.65rem', color: '#4a4a6a', fontFamily: 'monospace', marginTop: '0.2rem' }}>
                        {course.segments?.length || 0} módulos · {course.total_duration_minutes} min
                      </div>
                    </div>
                    <button type="button" onClick={() => handleAddCourseToTrail(course.id)} style={{ flexShrink: 0, display: 'inline-flex', alignItems: 'center', gap: '0.3rem', padding: '0.35rem 0.7rem', borderRadius: 7, background: 'rgba(59,130,246,0.1)', border: '1px solid rgba(59,130,246,0.22)', color: '#60a5fa', fontSize: '0.75rem', fontFamily: "'Inter', sans-serif", cursor: 'pointer', whiteSpace: 'nowrap' }}>
                      <Plus size={12} />
                      Adicionar
                    </button>
                  </div>
                ))}
              </div>
            )
          ) : null}

          <div style={{ display: 'flex', justifyContent: 'flex-end', paddingTop: '0.75rem', borderTop: '1px solid rgba(255,255,255,0.06)', marginTop: '0.5rem' }}>
            <button type="button" onClick={closeCoursePicker} style={{ padding: '0.4rem 0.9rem', borderRadius: 7, background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.09)', color: '#6a6a9a', fontSize: '0.8rem', fontFamily: "'Inter', sans-serif", cursor: 'pointer' }}>Fechar</button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={finalAssessmentModal.open} onOpenChange={(open) => { if (!open) closeFinalAssessmentPicker(); }}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto" style={{ background: '#12121a', border: '1px solid rgba(255,255,255,0.08)', color: '#e0e0f0' }}>
          <DialogHeader style={{ paddingBottom: '0.75rem', borderBottom: '1px solid rgba(255,255,255,0.06)', marginBottom: '0.5rem' }}>
            <DialogTitle style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', fontFamily: "'Space Grotesk', sans-serif", fontSize: '0.96rem', fontWeight: 700, color: '#e8e8f0' }}>
              <div style={{ width: 26, height: 26, borderRadius: 7, flexShrink: 0, background: 'rgba(59,130,246,0.1)', border: '1px solid rgba(59,130,246,0.2)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <Target size={13} style={{ color: '#60a5fa' }} />
              </div>
              Selecionar Avaliação Final
            </DialogTitle>
            <DialogDescription style={{ fontSize: '0.73rem', color: '#4a4a6a', fontFamily: "'Inter', sans-serif", paddingLeft: '2.35rem', marginTop: '0.1rem' }}>
              Vincular avaliação à trilha <span style={{ color: '#8a8ab0' }}>{finalAssessmentModal.trail?.name || "selecionada"}</span>
            </DialogDescription>
          </DialogHeader>

          {finalAssessmentModal.trail ? (
            availableAssessmentsForTrail.length === 0 ? (
              <div style={{ textAlign: 'center', padding: '2.5rem 0', fontSize: '0.82rem', color: '#4a4a6a', fontFamily: "'Inter', sans-serif" }}>
                Não há avaliações disponíveis para o setor desta trilha.
              </div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.4rem' }}>
                {availableAssessmentsForTrail.map(assessment => (
                  <div key={assessment.id} style={{ padding: '0.65rem 0.75rem', border: '1px solid rgba(255,255,255,0.07)', borderRadius: 8, background: 'rgba(255,255,255,0.02)' }}>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '1rem' }}>
                      <div style={{ minWidth: 0 }}>
                        <h4 style={{ fontSize: '0.82rem', fontWeight: 600, color: '#c0c0d8', fontFamily: "'Inter', sans-serif", margin: 0 }}>{assessment.title}</h4>
                        <p style={{ fontSize: '0.72rem', color: '#5a5a8a', fontFamily: "'Inter', sans-serif", margin: '0.15rem 0 0', overflow: 'hidden', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical' }}>{assessment.description}</p>
                        <div style={{ fontSize: '0.65rem', color: '#4a4a6a', fontFamily: 'monospace', marginTop: '0.2rem' }}>
                          Setores: {assessment.sectors?.length ? assessment.sectors.join(", ") : "Todos"}
                        </div>
                      </div>
                      <button type="button" onClick={() => handleLinkFinalAssessment(assessment.id)} style={{ flexShrink: 0, padding: '0.35rem 0.7rem', borderRadius: 7, background: 'rgba(59,130,246,0.1)', border: '1px solid rgba(59,130,246,0.22)', color: '#60a5fa', fontSize: '0.75rem', fontFamily: "'Inter', sans-serif", cursor: 'pointer', whiteSpace: 'nowrap' }}>
                        Selecionar
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )
          ) : null}

          <div style={{ display: 'flex', justifyContent: 'flex-end', paddingTop: '0.75rem', borderTop: '1px solid rgba(255,255,255,0.06)', marginTop: '0.5rem' }}>
            <button type="button" onClick={closeFinalAssessmentPicker} style={{ padding: '0.4rem 0.9rem', borderRadius: 7, background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.09)', color: '#6a6a9a', fontSize: '0.8rem', fontFamily: "'Inter', sans-serif", cursor: 'pointer' }}>Fechar</button>
          </div>
        </DialogContent>
      </Dialog>

      {showVideoUploadManager && (
        <VideoUploadManager
          onVideoUploaded={(videoId, videoData) => {
            setNewSegment(prev => ({
              ...prev,
              video_id: videoId,
              video_url: null,
              video_type: "local",
              title: videoData.title || prev.title
            }));
            setShowVideoUploadManager(false);
            toast.success("Vídeo e transcrição prontos!");
          }}
          onCancel={() => setShowVideoUploadManager(false)}
        />
      )}

      {/* Confirm Delete Dialog */}
      {confirmDialog.open && (
        <div className="fixed inset-0 z-50 flex items-center justify-center" style={{ background: 'rgba(0,0,0,0.7)' }}>
          <div className="max-w-sm w-full mx-4 rounded-xl p-6" style={{ background: '#12121a', border: '1px solid rgba(255,255,255,0.08)', boxShadow: '0 8px 32px rgba(0,0,0,0.5)' }}>
            <h3 style={{ fontSize: '0.96rem', fontWeight: 700, color: '#e8e8f0', fontFamily: "'Space Grotesk', sans-serif", marginBottom: '0.4rem' }}>{confirmDialog.title}</h3>
            <p style={{ fontSize: '0.8rem', color: '#6a6a9a', fontFamily: "'Inter', sans-serif", marginBottom: '1.5rem' }}>{confirmDialog.message}</p>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '0.5rem' }}>
              <button
                onClick={closeConfirmDialog}
                style={{ display: 'inline-flex', alignItems: 'center', padding: '0.42rem 0.95rem', borderRadius: 8, background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.09)', color: '#6a6a9a', fontSize: '0.79rem', fontWeight: 600, cursor: 'pointer', fontFamily: "'Inter', sans-serif" }}
              >
                Cancelar
              </button>
              <button
                onClick={() => { confirmDialog.onConfirm?.(); closeConfirmDialog(); }}
                style={{ display: 'inline-flex', alignItems: 'center', padding: '0.42rem 0.95rem', borderRadius: 8, background: 'rgba(239,68,68,0.15)', border: '1px solid rgba(239,68,68,0.3)', color: '#f87171', fontSize: '0.79rem', fontWeight: 600, cursor: 'pointer', fontFamily: "'Inter', sans-serif" }}
              >
                Excluir
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TrailsManager;
